import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut, UserCircle, Settings, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Link } from 'react-router-dom';
import { toast } from '@/utils/toast';
import { useTheme } from '@/contexts/ThemeContext';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

const UserMenu: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  if (!currentUser) {
    return (
      <div className={cn(
        "flex items-center gap-3",
        isMobile ? "flex-col w-full" : "md:flex"
      )}>
        <Link to="/login" className={isMobile ? "w-full" : ""}>
          <Button 
            variant="outline" 
            size={isMobile ? "default" : "sm"} 
            className={cn(
              "font-semibold border-[#D3E4FD]/50 bg-transparent",
              isMobile && "w-full mb-2",
              isDark
                ? "text-[#D3E4FD] hover:bg-[#D3E4FD]/10 hover:text-[#D3E4FD]"
                : "text-[#0066CC] hover:bg-[#D3E4FD]/30 hover:text-[#0066CC]"
            )}
          >
            Sign In
          </Button>
        </Link>
        <Link to="/signup" className={isMobile ? "w-full" : ""}>
          <Button 
            size={isMobile ? "default" : "sm"} 
            className={cn(
              "font-semibold",
              isMobile && "w-full",
              isDark 
                ? "bg-[#D3E4FD]/20 hover:bg-[#D3E4FD]/30 text-white" 
                : "bg-[#0066CC] hover:bg-[#0055AA] text-white"
            )}
          >
            Sign Up
          </Button>
        </Link>
      </div>
    );
  }
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full">
          <Avatar className={cn(
            "h-8 w-8 border ring-2",
            isDark 
              ? "border-[#D3E4FD]/30 ring-[#D3E4FD]/10" 
              : "border-[#D3E4FD]/70 ring-[#D3E4FD]/30"
          )}>
            <AvatarImage src={currentUser.photoURL || undefined} alt={currentUser.displayName || 'User'} />
            <AvatarFallback className={cn(
              "text-lg font-bold",
              isDark ? "bg-[#D3E4FD]/10 text-[#D3E4FD]" : "bg-[#D3E4FD]/50 text-[#0066CC]"
            )}>
              {currentUser.displayName ? getInitials(currentUser.displayName) : 'U'}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className={cn(
        "w-56 border rounded-xl shadow-lg",
        isDark 
          ? "bg-[#151833]/90 border-[#D3E4FD]/20 backdrop-blur-xl" 
          : "bg-white/95 backdrop-blur-xl border-[#D3E4FD]/50"
      )} align="end" forceMount>
        <DropdownMenuLabel className={cn(
          "font-normal",
          isDark ? "text-gray-300" : "text-gray-700"
        )}>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-bold leading-none">{currentUser.displayName || 'User'}</p>
            <p className={cn(
              "text-xs leading-none",
              isDark ? "text-gray-400" : "text-gray-500"
            )}>
              {currentUser.email}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className={isDark ? "bg-[#D3E4FD]/10" : "bg-[#D3E4FD]/30"} />
        
        <DropdownMenuGroup>
          <DropdownMenuItem
            onClick={() => navigate('/profile')}
            className={cn(
              "cursor-pointer flex items-center",
              isDark
                ? "hover:bg-[#D3E4FD]/5"
                : "hover:bg-[#D3E4FD]/10"
            )}
          >
            <UserCircle className="mr-2 h-4 w-4" />
            <span>Profile</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem
            onClick={() => navigate('/settings')}
            className={cn(
              "cursor-pointer flex items-center",
              isDark
                ? "hover:bg-[#D3E4FD]/5"
                : "hover:bg-[#D3E4FD]/10"
            )}
          >
            <Settings className="mr-2 h-4 w-4" />
            <span>Settings</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem
            onClick={() => navigate('/chat')}
            className={cn(
              "cursor-pointer flex items-center",
              isDark
                ? "hover:bg-[#D3E4FD]/5"
                : "hover:bg-[#D3E4FD]/10"
            )}
          >
            <MessageSquare className="mr-2 h-4 w-4" />
            <span>Chat</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>
        
        <DropdownMenuSeparator className={isDark ? "bg-[#D3E4FD]/10" : "bg-[#D3E4FD]/30"} />
        
        <DropdownMenuItem onClick={handleLogout} className={cn(
          "cursor-pointer",
          isDark
            ? "text-red-400 hover:bg-red-900/20 hover:text-red-300"
            : "text-red-500 hover:bg-red-50 hover:text-red-600"
        )}>
          <LogOut className="mr-2 h-4 w-4" />
          <span className="font-bold">Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserMenu;
